import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import * as d3 from 'd3';
import 'amcharts3/amcharts/amcharts';
import 'amcharts3/amcharts/themes/light';
import 'ammap3/ammap/ammap.css';
import 'ammap3/ammap/ammap';
import 'ammap3/ammap/maps/js/worldLow';
import AmCharts from '@amcharts/amcharts3-react';
import glamorous from 'glamorous';
import get from 'lodash/get';

import { getTooltipHtml } from '../helpers';

const COLORS = {
  outlineColor: '#c7c7c7',
  selectedAreasColor: '#fbd563',
  unlistedAreasColor: '#fff',
  noValueColor: '#d1d1d1',
  minValueColor: '#80dacc',
  maxValueColor: '#06594e',
  ballonColor: '#fff',
  balloonFillColor: '#33414B',
  ballonBorderColor: '#33414B',
};

const getMaxValue = (countries) => d3.max(countries, (country) => country.recallCount);
const getMinValue = (countries) => d3.min(countries, (country) => (country.recallCount > 0 ? country.recallCount : 9999));

const buildColors = ({ countries }) => d3.scaleLinear().domain([0, getMaxValue(countries)]).range([COLORS.minValueColor, COLORS.maxValueColor]);

const buildChartOptions = ({ countries, countryColors, isDragged, setDragged, intl, goToSearchPage }) => ({
  type: 'map',
  hideCredits: true,

  // Style
  theme: 'light',
  projection: 'mercator',
  areasSettings: {
    autoZoom: false,
    selectable: true, // On non-selectable maps, clickMapObject events are not fired for some reason
    selectedColor: COLORS.selectedAreasColor,
    outlineColor: COLORS.outlineColor,
    outlineAlpha: 2,
    unlistedAreasColor: COLORS.unlistedAreasColor,
    unlistedAreasOutlineColor: COLORS.outlineColor,
    unlistedAreasOutlineAlpha: 2,
  },

  // Tooltip
  balloon: {
    color: COLORS.ballonColor,
    fillColor: COLORS.balloonFillColor,
    fillAlpha: 0,
    borderColor: COLORS.ballonBorderColor,
    borderThickness: 0,
    adjustBorderColor: false,
    cornerRadius: 4,
    horizontalPadding: 0,
    verticalPadding: 0,
    shadowAlpha: 0,
    offsetY: 0,
    offsetX: 0,
    drop: false,
    showBullet: false,
  },

  // Data
  dataProvider: {
    map: 'worldLow',
    areas: countries.map((country) => ({
      id: country.code,
      color: country.recallCount === 0 ? COLORS.noValueColor : countryColors(country.recallCount),
      balloonText: getTooltipHtml(country.label, country.recallCount, intl),
      customData: country.link,
    })),
  },

  listeners: [
    {
      event: 'dragCompleted',
      method: () => {
        // As clickMapObject event fires on drag too (strange behavior from AmCharts), we set a variable for a small time to prevent
        // "click" actions just after a drag
        setDragged(true);
        setTimeout(() => { setDragged(false); }, 100);
      },
    },
    {
      event: 'clickMapObject',
      method: (event) => {
        if (!isDragged) {
          const dParams = get(event.mapObject.customData, 'params');
          goToSearchPage(dParams, intl);
        }
      },
    },
  ],
});

const buildLegend = ({ countries, countryColors, chartRef, isFullScreen, showVisualData }) => {
  const element = chartRef;
  if (isFullScreen && showVisualData) {
    // Get min and max value (> zero)
    const minValue = getMinValue(countries);
    const maxValue = getMaxValue(countries);
    // Sizes in pixels
    const rectHeight = 200;
    const rectWidth = 50;
    const wrapper = d3
      .select(element)
      .insert('svg', ':first-child')
      .attr('transform', 'translate(50, 0)')
      .attr('id', 'legend');
    wrapper
      .append('text')
      .attr('class', 'title  is-6')
      .text('Number of recalls per country');
    const legend = wrapper
      .append('defs')
      .append('svg:linearGradient')
      .attr('id', 'gradientId')
      .attr('x1', '100%')
      .attr('y1', '0%')
      .attr('x2', '100%')
      .attr('y2', '100%')
      .attr('spreadMethod', 'pad');
    legend
      .append('stop')
      .attr('offset', '0%')
      .attr('stop-color', 'rgb(199, 199, 199)')
      .attr('stop-opacity', 1);
    legend
      .append('stop')
      .attr('offset', '20%')
      .attr('stop-color', countryColors(minValue).toString())
      .attr('stop-opacity', 1);
    legend
      .append('stop')
      .attr('offset', '100%')
      .attr('stop-color', countryColors(maxValue).toString())
      .attr('stop-opacity', 1);
    wrapper
      .append('rect')
      .attr('width', rectWidth)
      .attr('height', rectHeight)
      .style('fill', 'url(#gradientId)')
      .attr('transform', 'translate(0,20)');
    const legendScale = d3
      .scaleLinear()
      .range([0, rectHeight])
      .domain([0, maxValue]);

    const axis = d3.axisRight(legendScale).ticks(5);

    wrapper
      .append('g')
      .attr('class', 'x axis')
      .attr('transform', 'translate(50,20)')
      .call(axis)
      .append('text')
      .attr('x', 0);
  }
};

class WorldMap extends Component {
  constructor(props) {
    super(props);
    const { dataset: { data: countries }, isFullScreen, showVisualData = true, intl, goToSearchPage } = props;
    this.countries = countries;
    this.isFullScreen = isFullScreen;
    this.showVisualData = showVisualData;
    this.intl = intl;
    this.goToSearchPage = goToSearchPage;

    this.isDragged = false;
    this.chartRef = null;

    this.setDragged = this.setDragged.bind(this);

    this.countryColors = buildColors(this);
    this.chartOptions = buildChartOptions(this);
  }

  componentDidMount() {
    buildLegend(this);
  }

  setDragged(isDragged) {
    this.isDragged = isDragged;
  }

  render() {
    const { isFullScreen } = this.props;
    return (
      <glamorous.Div
        innerRef={(ref) => { this.chartRef = ref; }}
        className="d3-chart"
        css={{
          display: 'flex',
          height: isFullScreen ? '100%' : 400,
          width: '100% !important',
        }}
      >
        <AmCharts.React
          style={{
            width: '100%',
            height: isFullScreen ? '100%' : '400px',
          }}
          options={this.chartOptions}
        />
      </glamorous.Div>
    );
  }
}
export default injectIntl(WorldMap);



// WEBPACK FOOTER //
// ./src/views/dashboard/chart/world-map/index.js